import { LAYOUT_HEADER_HEIGHT } from '@src/styles/dimensions';
import {
  Button,
  Flex,
  FormControlSizes,
  getSymbolsAcrossMarkets,
  Icon,
  IconName,
  isMultileg,
  isOrderRejected,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  type Order,
  OrderCardHeader,
  Popover,
  PopperHeader,
  useMixpanel,
  usePopoverState,
  useSecurity,
  useStrategiesContext,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { type MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTradingOrderDetailsRoute } from '../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../hooks/useAppLayoutId';
import { useLatestOrderAnalytics } from '../../hooks/useLatestOrderAnalytics';
import { TradePredictionsWrapper } from '../TradePredictions/TradePredictionsWrapper';
import { KeyMetricsCard } from './KeyMetricsCard';
import { TradingCard } from './TradingCard';

const TOOLTIP_WIDTH = '520px';
const TOOLTIP_HEIGHT = '450px';

export function OrderAnalyticsTooltip({
  order,
  close,
  onHeightChange,
}: {
  order: Order;
  close: () => void;
  onHeightChange?: () => void;
}) {
  const mixpanel = useMixpanel();
  const security = useSecurity(order?.Symbol);
  const { strategiesByName } = useStrategiesContext();
  const selectedStrategy = order.Strategy ? strategiesByName?.get(order.Strategy) : undefined;
  const underlyingSymbols = getSymbolsAcrossMarkets(order);
  const isOrderMultiLeg = isMultileg(security);
  const analytics = useLatestOrderAnalytics({
    order: order,
    tag: 'OrderAnalyticsColumn',
  });

  const navigate = useNavigate();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number>(0);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutId = useCurrentAppLayoutId();

  // ResizeObserver to monitor height changes
  useEffect(() => {
    if (!tooltipRef.current || !onHeightChange) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const currentHeight = entry.contentRect.height;
      const previousHeight = previousHeightRef.current;

      // Only trigger update if height actually changed
      if (Math.abs(currentHeight - previousHeight) > 1) {
        previousHeightRef.current = currentHeight;

        // Debounce the update to prevent rapid successive calls
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          onHeightChange();
        }, 16); // ~60fps debounce
      }
    });

    resizeObserver.observe(tooltipRef.current);

    return () => {
      resizeObserver.disconnect();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [onHeightChange]);

  const handleOpenAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      close();
      mixpanel.track(MixpanelEvent.ViewFullAnalytics, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.AnalyticsModal,
      });
      navigate(
        getTradingOrderDetailsRoute({
          orderID: order.OrderID,
          type: 'order',
          tab: 'analytics',
          layoutId,
        })
      );
    },
    [navigate, order.OrderID, close, mixpanel, layoutId]
  );

  return (
    <Flex
      ref={tooltipRef}
      flexDirection="column"
      gap="spacing8"
      w={TOOLTIP_WIDTH}
      minHeight={TOOLTIP_HEIGHT}
      maxHeight={`calc(100vh - ${LAYOUT_HEADER_HEIGHT * 2}px)`}
      overflow="auto"
      data-testid="order-analytics"
    >
      <PopperHeader
        icon={IconName.ChartLine}
        title="Analytics"
        suffix={
          <Button startIcon={IconName.Exit} size={FormControlSizes.Small} onClick={handleOpenAnalytics}>
            View Full Analytics
          </Button>
        }
        onCloseClicked={close}
      />
      <Flex flexDirection="column" p="spacing8" gap="spacing4">
        <OrderCardHeader order={order} selectedStrategy={selectedStrategy} underlyingSymbols={underlyingSymbols} />
        <TradePredictionsWrapper
          order={order}
          isOrderMultiLeg={isOrderMultiLeg}
          latestAnalytics={analytics.latestAnalytics}
        />
        <KeyMetricsCard isMultileg={isOrderMultiLeg} order={order} analytics={analytics} />
        <TradingCard order={order} isMultileg={isOrderMultiLeg} analytics={analytics} />
      </Flex>
    </Flex>
  );
}

export function OrderAnalyticsTooltipCellRenderer({ data }: ICellRendererParams<Order>) {
  const mixpanel = useMixpanel();
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'right',
    delay: 500,
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'maxHeight',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
    onOpen: () => {
      mixpanel.track(MixpanelEvent.ViewAnalyticsTooltip, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
      });
    },
  });

  const handleHeightChange = useCallback(() => {
    if (popover.update) {
      popover.update();
    }
  }, [popover]);

  if (!data || !data.OrderID || isOrderRejected(data.OrdStatus)) {
    return null;
  }

  return (
    <Popover {...popover}>
      <Icon icon={IconName.ChartLine} />
      <OrderAnalyticsTooltip order={data} close={popover.close} onHeightChange={handleHeightChange} />
    </Popover>
  );
}
